import { useEffect, useState } from 'react'
import { useAdminApi } from '../api'
import { t } from '../i18n'
import { useLang } from '../LangContext'
import { UserFormModal, UserFormValues } from './UserFormModal'
import styles from './UsersPage.module.css'
import { Icon } from '../../styles/Icon'

interface Usuario {
  id: string
  nombre_visible: string
  email: string
  roles: string[]
  categorias_asignadas: string[]
  equipo_ids: string[]
  orden: number
}

export function UsersPage() {
  const api = useAdminApi()
  const lang = useLang()
  const [users, setUsers] = useState<Usuario[]>([])
  const [modalUser, setModalUser] = useState<Usuario | 'new' | null>(null)
  const [resetOk, setResetOk] = useState(false)
  const [dragId, setDragId] = useState<string | null>(null)

  async function refresh() {
    setUsers(await api.get('/admin/users'))
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleSave(values: UserFormValues) {
    if (modalUser === 'new') {
      await api.post('/admin/users', values)
    } else if (modalUser) {
      await api.patch(`/admin/users/${modalUser.id}`, values)
    }
    setModalUser(null)
    await refresh()
  }

  async function handleResetPassword(user: Usuario) {
    setResetOk(false)
    await api.post(`/admin/users/${user.id}/reset-password`)
    setResetOk(true)
  }

  // Issue #57, 2026-10-05: "quitamos la opción de Deshabilitar y añadimos un botón de Eliminar que
  // permita borrar completamente al usuario" — igual criterio de confirmación reforzada (escribir
  // el email exacto) ya usado para el borrado forzado de un equipo.
  async function handleDelete(user: Usuario) {
    if (!window.confirm(t('eliminarUsuarioConfirm', lang))) return
    const escrito = window.prompt(t('eliminarUsuarioEscribirEmail', lang).replace('{email}', user.email))
    if (escrito !== user.email) {
      if (escrito !== null) window.alert(t('eliminarUsuarioNoCoincide', lang))
      return
    }
    await api.del(`/admin/users/${user.id}`)
    await refresh()
  }

  // Issue #57, 2026-10-05: "debe poder reordenarse la lista de usuarios arrastrando y soltando" —
  // mismo patrón ya usado para equipos y miembros de plantilla.
  async function handleDrop(targetId: string) {
    if (!dragId || dragId === targetId) return
    const ordenados = [...users].sort((a, b) => a.orden - b.orden)
    const fromIndex = ordenados.findIndex((u) => u.id === dragId)
    const toIndex = ordenados.findIndex((u) => u.id === targetId)
    const reordenados = [...ordenados]
    const [moved] = reordenados.splice(fromIndex, 1)
    reordenados.splice(toIndex, 0, moved)
    setDragId(null)
    await Promise.all(reordenados.map((u, index) => api.patch(`/admin/users/${u.id}`, { orden: index })))
    await refresh()
  }

  return (
    <div>
      <h2>{t('usuarios', lang)}</h2>
      {resetOk && (
        <div role="status" className={styles.successBanner}>
          {t('resetPasswordOk', lang)}
        </div>
      )}
      <button onClick={() => setModalUser('new')}><Icon name="crear" />{t('crear', lang)}</button>
      <div className={styles.tableWrap}>
        <table className="cards-on-mobile sticky-first-col">
          <thead>
            <tr>
              <th>{t('nombre', lang)}</th>
              <th>{t('email', lang)}</th>
              <th>{t('rol', lang)}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {[...users]
              .sort((a, b) => a.orden - b.orden)
              .map((user) => (
                <tr
                  key={user.id}
                  draggable
                  onDragStart={() => setDragId(user.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(user.id)}
                >
                  <td data-label={t('nombre', lang)}>{user.nombre_visible}</td>
                  <td data-label={t('email', lang)}>{user.email}</td>
                  <td data-label={t('rol', lang)}>{user.roles.join(', ')}</td>
                  <td className={styles.actionsCell}>
                    <button onClick={() => setModalUser(user)}><Icon name="editar" />{t('editar', lang)}</button>
                    <button onClick={() => handleResetPassword(user)}><Icon name="restablecer" />{t('resetPassword', lang)}</button>
                    <button className={styles.dangerButton} data-variant="danger" onClick={() => handleDelete(user)}>
                      <Icon name="eliminar" />{t('eliminar', lang)}
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {modalUser && (
        <UserFormModal
          initial={modalUser === 'new' ? undefined : modalUser}
          onSave={handleSave}
          onClose={() => setModalUser(null)}
        />
      )}
    </div>
  )
}
